import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Menu, Search, User, ShoppingCart, LogOut } from 'lucide-react';
import { useCategoriesWithProducts, useOccasionsWithProducts } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';
import { useCityNavigation } from '@/hooks/useCityNavigation';
import { Button } from '@/components/ui/button';
import CartDrawer from './CartDrawer';
import UserMenu from './header/UserMenu';
import SearchBar from './header/SearchBar';
import { DeliveryLocationSelector } from './header/DeliveryLocationSelector';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from './ui/navigation-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { useRef } from 'react';
import MobileMenu from './header/MobileMenu';
import { Sheet, SheetContent } from '@/components/ui/sheet';

const translations = {
  en: {
    searchPlaceholder: 'Search for flowers...',
    trackOrder: 'Track Order',
    cart: 'Cart',
    signIn: 'Sign In',
    logout: 'Logout',
    profile: 'Profile',
    myOrders: 'My Orders',
    more: 'More',
    about: 'About',
    contact: 'Contact',
    faq: 'FAQ',
  },
  ar: {
    searchPlaceholder: 'ابحث عن الورد...',
    trackOrder: 'تتبع الطلب',
    cart: 'السلة',
    signIn: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    profile: 'الملف الشخصي',
    myOrders: 'طلباتي',
    more: 'المزيد',
    about: 'عن الموقع',
    contact: 'اتصل بنا',
    faq: 'الأسئلة الشائعة',
  }
};

interface FlowerAuraHeaderProps {
  language?: string;
}

const FlowerAuraHeader = ({ language: propLanguage }: FlowerAuraHeaderProps = {}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { navigateWithCity } = useCityNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  // Only categories + occasions that actually have products. Empty ones
  // (gifts, chocolates, cakes, sorry-apology, etc.) used to dead-end on
  // "Category not found" / "No products found for this occasion."
  const { data: categories = [], isLoading: categoriesLoading } = useCategoriesWithProducts();
  const { data: occasions = [], isLoading: occasionsLoading } = useOccasionsWithProducts();
  const { state } = useCart();
  const { user, signOut } = useAuth();
  const context = useLanguage();
  const language = propLanguage || context.language;
  const setLanguage = context.setLanguage;
  const t = translations[language];
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  const handleLanguageSwitch = (lang: 'en' | 'ar') => {
    setLanguage(lang);
    const path = location.pathname;
    // Match only the first /en-eg/:citySlug or /ar-eg/:citySlug segment
    const match = path.match(/^\/(en-eg|ar-eg)\/([^/]+)(\/.*)?$/);
    if (match) {
      const currentPrefix = match[1];
      const citySlug = match[2];
      const rest = match[3] || '';
      const newPrefix = lang === 'ar' ? 'ar-eg' : 'en-eg';
      const newPath = `/${newPrefix}/${citySlug}${rest}${location.search}`;
      if (path !== newPath) {
        window.location.replace(newPath); // Force full reload
      }
    }
  };

  // Dynamic nav: only categories that have at least one product, plus
  // every occasion. Cap at 11 to keep the bar from wrapping on laptop
  // widths. Always include /flowers as the first item.
  const dynamicNavLinks = [
    {
      type: 'static' as const,
      label: language === 'ar' ? 'ورد' : 'Flowers',
      path: '/flowers',
    },
    ...categories.map((cat: any) => ({
      type: 'category' as const,
      label: language === 'ar' ? (cat.name_ar || cat.name_en) : cat.name_en,
      path: `/category/${cat.slug}`,
    })),
    ...occasions.map((occ: any) => ({
      type: 'occasion' as const,
      label: language === 'ar' ? (occ.name_ar || occ.name_en) : occ.name_en,
      path: `/occasion/${occ.slug}`,
    })),
  ].slice(0, 11);

  return (
    <header className={`bg-background border-b shadow-sm`}>
      {/* Top bar */}
      <div className="bg-muted py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <DeliveryLocationSelector language={language} />
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => handleLanguageSwitch('en')}
              className={`hover:text-primary ${language === 'en' ? 'text-foreground font-semibold' : ''}`}
            >
              EN
            </button>
            <span>|</span>
            <button 
              onClick={() => handleLanguageSwitch('ar')}
              className={`hover:text-primary ${language === 'ar' ? 'text-foreground font-semibold' : ''}`}
            >
              العربية
            </button>
          </div>
        </div>
      </div>

      {/* Mobile header */}
      <div className="md:hidden container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-2">
          {/* Hamburger menu */}
          <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(true)} aria-label="Open menu">
            <Menu className="h-7 w-7" />
          </Button>
          {/* Search icon */}
          <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)} aria-label="Search">
            <Search className="h-7 w-7" />
          </Button>
          {/* Logo (centered) */}
          <div className="flex-1 flex justify-center">
            <button onClick={() => navigateWithCity('/')} className="focus:outline-none">
              <img 
                src="/lovable-uploads/5e9a3006-4daa-4740-a8bf-b7ec5abc7fa3.webp" 
                alt="Bouqueh" 
                className="h-12 w-auto"
                decoding="async"
                fetchPriority="high"
                sizes="167px"
                width="167"
                height="48"
                style={{ aspectRatio: '167/48' }}
              />
            </button>
          </div>
          {/* User icon */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="User">
                  <User className="h-7 w-7" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigateWithCity('/profile')}>
                  <User className="h-4 w-4 mr-2" />
                  {t.profile}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigateWithCity('/profile?tab=orders')}>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {t.myOrders}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigateWithCity('/profile?tab=occasions')}>
                  <MapPin className="h-4 w-4 mr-2" />
                  My Occasions
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={async () => {
                    try {
                      await signOut();
                      // Optionally show success message or redirect
                    } catch (error) {
                      console.warn('Logout error:', error);
                      // Still clear local state even if server logout fails
                    }
                  }} 
                  className="text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="icon" onClick={() => navigateWithCity('/auth')} aria-label="User">
              <User className="h-7 w-7" />
            </Button>
          )}
          {/* Cart icon */}
          <CartDrawer
            trigger={
              <Button variant="ghost" size="icon" aria-label="Cart" className="relative">
                <ShoppingCart className="h-7 w-7" />
                {state.itemCount > 0 && (
                  <span
                    className="absolute -top-1 -right-2 bg-destructive text-destructive-foreground rounded-full h-5 w-5 flex items-center justify-center text-xs shadow border-2 border-white z-20"
                    style={{ minWidth: 20 }}
                  >
                    {state.itemCount}
                  </span>
                )}
              </Button>
            }
            language={language as 'en' | 'ar'}
          />
        </div>
        {/* Mobile search drawer */}
        <Sheet open={isSearchOpen} onOpenChange={setIsSearchOpen}>
          <SheetContent className="w-full sm:max-w-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-lg">{t.searchPlaceholder}</span>
            </div>
            <SearchBar placeholder={t.searchPlaceholder} language={language} />
          </SheetContent>
        </Sheet>
        {/* Mobile menu drawer */}
        <MobileMenu 
          isOpen={isMenuOpen} 
          translations={{
            flowers: language === 'ar' ? 'ورد' : 'Flowers',
            gifts: language === 'ar' ? 'هدايا' : 'Gifts',
            occasions: language === 'ar' ? 'مناسبات' : 'Occasions',
            contact: language === 'ar' ? 'اتصل بنا' : 'Contact',
            login: language === 'ar' ? 'تسجيل الدخول' : 'Sign In',
            logout: language === 'ar' ? 'تسجيل الخروج' : 'Logout',
            profile: language === 'ar' ? 'الملف الشخصي' : 'Profile',
            orders: language === 'ar' ? 'طلباتي' : 'My Orders',
          }} 
          onClose={() => setIsMenuOpen(false)} 
        />
      </div>

      {/* Desktop header (unchanged) */}
      <div className="hidden md:block">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between gap-8">
          {/* Logo */}
          <button onClick={() => navigateWithCity('/')} className="focus:outline-none">
            <img 
              src="/lovable-uploads/5e9a3006-4daa-4740-a8bf-b7ec5abc7fa3.webp" 
              alt="Bouqueh" 
              className="h-12 w-auto"
              decoding="async"
              fetchPriority="high"
              sizes="167px"
              width="167"
              height="48"
              style={{ aspectRatio: '167/48' }}
            />
          </button>
          {/* Search bar (centered) */}
          <div className="flex-1 max-w-xl mx-8">
            <SearchBar placeholder={t.searchPlaceholder} language={language} />
          </div>
          {/* Action icons */}
          <div className="flex items-center gap-8 text-sm">
            <div className="flex flex-col items-center gap-1 cursor-pointer hover:text-primary">
              <button onClick={() => navigateWithCity('/track-order')} className="flex flex-col items-center gap-1 focus:outline-none bg-transparent border-0 p-0">
                <MapPin className="h-6 w-6" style={{ strokeWidth: 1.5 }} />
                <span className="text-base font-normal leading-none">{t.trackOrder}</span>
              </button>
            </div>

            <div className="flex flex-col items-center gap-1 cursor-pointer hover:text-primary">
              <CartDrawer 
                language={language as 'en' | 'ar'}
                trigger={
                  <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 relative group hover:text-primary">
                    <img src="/cart-icon.svg" alt={language === 'ar' ? 'سلة التسوق' : 'Shopping cart'} className="h-6 w-6 group-hover:text-primary" style={{ strokeWidth: 1.5 }} />
                    <span className="text-base font-normal leading-none group-hover:text-primary">{t.cart}</span>
                    {state.itemCount > 0 && (
                      <span
                        className="absolute -top-1 -right-2 bg-destructive text-destructive-foreground rounded-full h-5 w-5 flex items-center justify-center text-xs shadow"
                        style={{ minWidth: 20 }}
                      >
                        {state.itemCount}
                      </span>
                    )}
                  </Button>
                }
              />
            </div>
            <div className="flex flex-col items-center gap-1 cursor-pointer hover:text-primary">
              <UserMenu translations={{
                login: t.signIn,
                logout: t.logout,
                profile: t.profile,
                orders: t.myOrders
              }} />
            </div>
            <div className="flex flex-col items-center gap-1 cursor-pointer hover:text-primary">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex flex-col items-center gap-1 focus:outline-none bg-transparent border-0 p-0">
                    <Menu className="h-6 w-6" style={{ strokeWidth: 1.5 }} />
                    <span className="text-base font-normal leading-none">{t.more}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigateWithCity('/about')}>{t.about}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigateWithCity('/contact')}>{t.contact}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigateWithCity('/faq')}>{t.faq}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation menu */}
      <div className="border-t bg-background">
        <div className="container mx-auto px-4 flex justify-center">
          <NavigationMenu>
            <NavigationMenuList className="hidden lg:flex justify-center">
              {dynamicNavLinks.map((item) => (
                <NavigationMenuItem key={item.path}>
                  <NavigationMenuLink asChild>
                    <button onClick={() => navigateWithCity(item.path)} className="text-base font-medium px-4 py-2 hover:bg-accent rounded transition-colors">
                      {item.label}
                    </button>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>
    </header>
  );
};

export default FlowerAuraHeader;